import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Spinner from '@cloudscape-design/components/spinner';
import type { UsageDocResult } from '@/lib/firestoreTypes';

type Props = {
  usage: UsageDocResult;
  yyyymm: string;
};

/**
 * Card de uso do mês (admin, Cloudscape). Substitui o `UsageCard` do ui-shared
 * (Tailwind). Mostra o total de etiquetas impressas no mês.
 *
 * Este é o contador **A** (`billing/usage/months`, UTC, ancorado no `Date.now()`
 * do trigger). **Não bate com o total que o cliente vê no Monitor**, que lê
 * `daily_ops` no fuso da empresa. O `helper` abaixo do número existe para o
 * operador não tratar a diferença como bug no atendimento — as quatro
 * divergências estão em `docs/label-counters.md`.
 */
export function UsageCard({ usage, yyyymm }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const printed = usage.kind === 'READY' ? usage.data.labelsPrinted : 0;

  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            <Box variant="span" color="text-status-inactive">
              <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{yyyymm}</span>
            </Box>
          }
        >
          {t('tenantDetail.usage.title')}
        </Header>
      }
    >
      {usage.kind === 'ERROR' ? (
        <Alert type="error">{t('tenantDetail.usage.errorLoad')}</Alert>
      ) : usage.kind === 'LOADING' ? (
        <Spinner />
      ) : (
        <div>
          <Box variant="awsui-key-label">{t('tenantDetail.usage.printed')}</Box>
          <Box variant="h1">{printed}</Box>
          <Box variant="small" color="text-status-inactive">
            {t('tenantDetail.usage.helper')}
          </Box>
        </div>
      )}
    </Container>
  );
}
